import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase/client'
import { mapEventRow, toEventRowInput, type EventInput, type EventRecord, type EventRow } from './types'
import { createEventReadCache, type EventReadCache } from './eventReadCache'

export type EventServiceErrorCode = 'offline' | 'not-found' | 'read-failed' | 'has-dependencies' | 'unexpected' | 'invalid'

export class EventServiceError extends Error {
  constructor(public readonly code: EventServiceErrorCode, message: string) {
    super(message)
    this.name = 'EventServiceError'
  }
}

export interface EventService {
  list(): Promise<EventRecord[]>
  get(id: string): Promise<EventRecord>
  /** Present only after a read has completed; used to explain offline data. */
  readStatus?(): EventReadStatus
  create(input: EventInput): Promise<EventRecord>
  update(id: string, input: EventInput): Promise<EventRecord>
  delete(id: string): Promise<void>
}

export type EventReadStatus = Readonly<{ source: 'network' | 'cache'; cachedAt?: string }>

type EventServiceOptions = Readonly<{
  cache?: EventReadCache
  getUserId?: () => Promise<string | null>
}>

function ensureOnline() {
  if (!navigator.onLine) {
    throw new EventServiceError('offline', 'A connection is required. Nothing has been queued.')
  }
}

/**
 * Reads queue nothing, so "Nothing has been queued" would be a non-sequitur
 * here. Failing before the request also spares the reader the ~7 s of retries
 * a dead connection costs, during which these screens show nothing but a
 * skeleton.
 */
function safeError(error: { code?: string; message?: string } | null): never {
  if (error?.code === 'PGRST116') throw new EventServiceError('not-found', 'Event not found.')
  // The database's check on the ticket text says exactly what to fix.
  if (error?.code === '22023' && error.message?.startsWith('ticket message: ')) {
    const problem = error.message.slice('ticket message: '.length)
    throw new EventServiceError('invalid', `The ticket message can’t be saved: ${problem}.`)
  }
  if (error?.code === '23503') {
    throw new EventServiceError('has-dependencies', 'This event cannot be deleted because protected operational records depend on it.')
  }
  throw new EventServiceError('unexpected', 'The event could not be saved. Review the event details and try again.')
}

/**
 * Reads fail for reasons that have nothing to do with what the user typed, so
 * they never share the write message: telling someone their event "could not be
 * saved" after a list or detail fetch failed implies data was lost when nothing
 * was ever being written.
 */
function readError(error: { code?: string } | null, message: string): never {
  if (error?.code === 'PGRST116') throw new EventServiceError('not-found', 'Event not found.')
  throw new EventServiceError('read-failed', message)
}

export function createSupabaseEventService(client: SupabaseClient = supabase, options: EventServiceOptions = {}): EventService {
  const cache = options.cache ?? createEventReadCache()
  let status: EventReadStatus = { source: 'network' }
  const getUserId = options.getUserId ?? (async () => {
    const auth = client.auth
    if (!auth) return null
    const { data } = await auth.getSession()
    return data.session?.user.id ?? null
  })

  async function cachedEvents() {
    try {
      const userId = await getUserId()
      if (!userId) return undefined
      return await cache.read(userId)
    } catch {
      return undefined
    }
  }

  async function saveEvents(events: EventRecord[]) {
    try {
      const userId = await getUserId()
      if (userId) await cache.save(userId, events)
    } catch {
      // A browser may refuse IndexedDB (for example in a private context). A
      // successful online read must still be shown in that case.
    }
  }

  async function updateCachedEvent(event: EventRecord) {
    try {
      const userId = await getUserId()
      if (userId) await cache.upsert(userId, event)
    } catch {
      // See saveEvents: caching improves offline use but never blocks a write.
    }
  }

  return {
    async list() {
      if (!navigator.onLine) {
        const cached = await cachedEvents()
        if (!cached) throw new EventServiceError('offline', 'No previously loaded events are available offline.')
        status = { source: 'cache', cachedAt: cached.cachedAt }
        return cached.events
      }
      const result = await client.from('events').select('*').order('event_date', { ascending: false })
      if (result.error) readError(result.error, 'Events could not be loaded. Check your connection and try again.')
      const events = ((result.data as EventRow[] | null) ?? []).map(mapEventRow)
      await saveEvents(events)
      status = { source: 'network' }
      return events
    },
    async get(id) {
      if (!navigator.onLine) {
        const cached = await cachedEvents()
        const event = cached?.events.find((item) => item.id === id)
        if (!cached || !event) throw new EventServiceError('offline', 'This event has not been loaded on this device yet.')
        status = { source: 'cache', cachedAt: cached.cachedAt }
        return event
      }
      const result = await client.from('events').select('*').eq('id', id).single()
      if (result.error) readError(result.error, 'This event could not be loaded. Check your connection and try again.')
      const event = mapEventRow(result.data as EventRow)
      await updateCachedEvent(event)
      status = { source: 'network' }
      return event
    },
    readStatus: () => status,
    async create(input) {
      ensureOnline()
      const result = await client.from('events').insert(toEventRowInput(input)).select('*').single()
      if (result.error) safeError(result.error)
      const event = mapEventRow(result.data as EventRow)
      await updateCachedEvent(event)
      return event
    },
    async update(id, input) {
      ensureOnline()
      const result = await client.from('events').update(toEventRowInput(input)).eq('id', id).select('*').single()
      if (result.error) safeError(result.error)
      const event = mapEventRow(result.data as EventRow)
      await updateCachedEvent(event)
      return event
    },
    async delete(id) {
      ensureOnline()
      const { error } = await client.from('events').delete().eq('id', id)
      if (error) safeError(error)
      try {
        const userId = await getUserId()
        if (userId) await cache.remove(userId, id)
      } catch {
        // The server deletion succeeded; an unavailable cache is non-fatal.
      }
    },
  }
}
